import { useTranslation } from "react-i18next";
import { IconArrowUpRight } from "@tabler/icons-react";
import type { Api } from "../../services/api";
import type { SupportedLanguage } from "../../interfaces";
import { formatCongressDay } from "../../domains/congress/congressDateLabel";

type TopSession =
  Api["get"]["/webapp/private/congress-spaces/:id/top-sessions"]["output"]["body"]["sessions"][number];

const DAY_MS = 86_400_000;

function utcDay(date: Date): number {
  return Math.floor(date.getTime() / DAY_MS);
}

/**
 * One session of the curator's Top 5 on the space page, as the DS
 * "Congress-session-card" (Figma "04 - Rooms / Congress"): its rank, the
 * title, where and when (today's sessions read "Today", and the ranks that
 * start at the same time are named during the congress), the curator's
 * takeaway and the link to the session.
 *
 * Times are UTC, the congress's own calendar, like every congress surface.
 */
export function DesktopCongressSessionCard({
  rank,
  session,
  phase,
  sameTimeAs,
  contentLang,
  now,
  onLinkClick,
}: {
  rank: number;
  session: TopSession;
  phase: "pre" | "live" | "post";
  /** Ranks of the other sessions that start at the same time */
  sameTimeAs: number[];
  contentLang: SupportedLanguage;
  now: Date;
  onLinkClick: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const startsAt = new Date(session.startsAt);
  const isToday = phase === "live" && utcDay(startsAt) === utcDay(now);
  const time = startsAt.toLocaleTimeString(contentLang, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  });
  const where = [
    session.roomLocation,
    `${isToday ? t("Today") : formatCongressDay(startsAt, contentLang)} ${time}`,
    ...(sameTimeAs.length === 0 || phase === "post"
      ? []
      : [t("same time as {{ranks}}", { ranks: sameTimeAs.join(", ") })]),
  ].join(" · ");

  return (
    <div className="flex flex-col items-start gap-2 rounded-2xl border border-ds-slate-100 bg-ds-canvas p-4 font-dm-sans">
      <div className="flex w-full items-start gap-3">
        <span className="flex size-6.5 shrink-0 items-center justify-center rounded-full bg-ds-blackberry-600 text-footnote font-bold text-ds-white">
          {rank}
        </span>
        <div className="flex min-w-0 flex-1 flex-col pt-0.75">
          <p className="m-0 text-footnote font-bold text-ds-black">
            {session.title}
          </p>
          <p className="m-0 text-caption text-ds-slate-500">{where}</p>
        </div>
      </div>
      <p className="m-0 text-footnote text-ds-black">“{session.takeaway}”</p>
      <a
        href={session.sessionUrl}
        target="_blank"
        rel="noreferrer"
        onClick={onLinkClick}
        className="inline-flex items-center gap-0.5 text-caption font-medium text-ds-mint-700 no-underline hover:underline"
      >
        {t("See session details")}
        <IconArrowUpRight className="size-3" stroke={1.75} />
      </a>
    </div>
  );
}
